import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

// Import Assets
import bgImage from '../../assets/Image_section_Wbg.png';
import coffeeCup from '../../assets/Coffee.png';
import chaiCup from '../../assets/Chai.png';
import matchaCup from '../../assets/Macha.png';
import latteCup from '../../assets/Latte.png';
import croissantPlate from '../../assets/Croissant.png';
import coldCoffeeCup from '../../assets/Cold_coffee.png';

const drinks = [
  { name: 'Coffee', image: coffeeCup },
  { name: 'Chai', image: chaiCup },
  { name: 'Matcha', image: matchaCup },
  { name: 'Latte', image: latteCup },
  { name: 'Croissant', image: croissantPlate },
  { name: 'Cold Coffee', image: coldCoffeeCup },
];

const DrinkShowcase: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);

  const handleNext = () => {
    if (isAnimating) return; // Prevent spam clicking
    setIsAnimating(true);
    setDirection('left'); // Current image exits to left

    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % drinks.length);
      setDirection('right'); // New image enters from right
      
      // Small delay to allow render before sliding in
      setTimeout(() => {
        setDirection(null); // Reset to center
        setIsAnimating(false);
      }, 50);
    }, 300); // Duration of exit animation
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection('right'); // Current image exits to right

    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + drinks.length) % drinks.length);
      setDirection('left'); // New image enters from left
      
      setTimeout(() => {
        setDirection(null);
        setIsAnimating(false);
      }, 50);
    }, 300);
  };

  // Calculate transform based on direction state
  const getTransform = () => {
    if (direction === 'left') return 'translateX(-150%)'; // Exit Left / Enter from Left position
    if (direction === 'right') return 'translateX(150%)'; // Exit Right / Enter from Right position
    return 'translateX(0)'; // Center
  };

  // Calculate opacity
  const getOpacity = () => {
    // If direction is set (moving), opacity goes to 0. 
    // But we need a 2-step process: Exit (opacity 1->0), Enter (opacity 0->1)
    // The current logic handles the "Exit" phase. 
    // The "Enter" phase is handled by the state reset in setTimeout.
    return direction ? 0 : 1;
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      maxWidth: '1200px', 
      aspectRatio: '16/9', 
      mx: 'auto', 
      my: 8,
      borderRadius: '24px', 
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      bgcolor: '#000' // Fallback bg
    }}>
      {/* 1. Base Background Image */}
      <Box
        component="img"
        src={bgImage}
        alt="Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
        }}
      />

      {/* 2. The Drink Image Container */}
      <Box
        sx={{
          position: 'absolute',
          top: '70%',
          left: '54%',
          // We use margin-left/top to center instead of translate to avoid conflict with animation transform
          marginTop: '-12%', // Adjust based on image height aspect ratio
          marginLeft: '-12%', // Adjust based on image width
          width: '20%', 
          height: 'auto',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          
          // Animation Properties
          transition: 'all 0.3s ease-in-out',
          transform: getTransform(),
          opacity: direction ? 0 : 1, 
        }}
      >
        <Box
          component="img"
          src={drinks[currentIndex].image}
          alt={drinks[currentIndex].name}
          sx={{
            width: '100%',
            height: 'auto',
            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))',
            display: 'block'
          }}
        />
        {/* Drink Name */}
        <Typography
          variant="h4"
          sx={{
            color: '#C4B66F', // Gold color
            fontWeight: 800,
            textShadow: '0 4px 8px rgba(0,0,0,0.9)',
            mt: 3,
            fontFamily: '"Inter", sans-serif',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          {drinks[currentIndex].name}
        </Typography>
      </Box>

      {/* 4. Navigation Buttons */}
      <IconButton
        onClick={handlePrev}
        disabled={isAnimating}
        sx={{
          position: 'absolute',
          left: 30,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: '#C4B66F',
          border: '1px solid rgba(196, 182, 111, 0.3)',
          width: 60,
          height: 60,
          backdropFilter: 'blur(5px)',
          '&:hover': { 
            bgcolor: 'rgba(0,0,0,0.8)',
            borderColor: '#C4B66F',
            transform: 'translateY(-50%) scale(1.1)'
          },
          transition: 'all 0.2s'
        }}
      >
        <ChevronLeft sx={{ fontSize: 40 }} />
      </IconButton>

      <IconButton
        onClick={handleNext}
        disabled={isAnimating}
        sx={{
          position: 'absolute',
          right: 30,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: '#C4B66F',
          border: '1px solid rgba(196, 182, 111, 0.3)',
          width: 60,
          height: 60,
          backdropFilter: 'blur(5px)',
          '&:hover': { 
            bgcolor: 'rgba(0,0,0,0.8)',
            borderColor: '#C4B66F',
            transform: 'translateY(-50%) scale(1.1)'
          },
          transition: 'all 0.2s'
        }}
      >
        <ChevronRight sx={{ fontSize: 40 }} />
      </IconButton>
    </Box>
  );
};

export default DrinkShowcase;